import React, { useState, useEffect } from 'react';
import collegeConfig from '../../config/collegeConfig';
import {
  FaHospital, FaFileAlt, FaClipboardCheck, FaToggleOn, FaToggleOff,
  FaSpinner, FaCheckCircle, FaInfoCircle
} from 'react-icons/fa';

const API = collegeConfig.api.baseURL;

const ToggleCard = ({ icon, title, description, enabled, saving, onToggle, enabledLabel = 'Currently Enabled', disabledLabel = 'Currently Disabled' }) => (
  <div style={s.card}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        {icon && <span style={{ color: '#E67E22', fontSize: 18, marginTop: 2 }}>{icon}</span>}
        <div>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e', marginBottom: 4 }}>{title}</div>
          <div style={{ fontSize: 13, color: '#666', lineHeight: 1.5 }}>{description}</div>
        </div>
      </div>
      <button
        onClick={onToggle}
        disabled={saving}
        style={{ background: 'none', border: 'none', cursor: saving ? 'not-allowed' : 'pointer', padding: 0, flexShrink: 0, marginLeft: 24 }}
      >
        {saving
          ? <FaSpinner style={{ fontSize: 36, color: '#b11217', animation: 'spin 1s linear infinite' }} />
          : enabled
            ? <FaToggleOn style={{ fontSize: 42, color: '#059669' }} />
            : <FaToggleOff style={{ fontSize: 42, color: '#ccc' }} />
        }
      </button>
    </div>
    <div style={{
      marginTop: 14,
      padding: '8px 14px',
      borderRadius: 8,
      background: enabled ? '#d1fae5' : '#f3f4f6',
      color: enabled ? '#065f46' : '#6b7280',
      fontSize: 13,
      fontWeight: 600,
      display: 'inline-block'
    }}>
      {enabled ? enabledLabel : disabledLabel}
    </div>
  </div>
);

const SystemSettings = () => {
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');

  const [drpMode, setDrpMode] = useState(null);
  const [savingDrp, setSavingDrp] = useState(false);

  const [internalTestEnabled, setInternalTestEnabled] = useState(true);
  const [savingInternalTest, setSavingInternalTest] = useState(false);

  const [weeklyLogbookHidden, setWeeklyLogbookHidden] = useState(false);
  const [savingWeeklyLogbook, setSavingWeeklyLogbook] = useState(false);

  const token = localStorage.getItem('token');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  useEffect(() => {
    fetch(`${API}/system-settings`)
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setDrpMode(d.drpMode);
          setInternalTestEnabled(d.internalTestEnabled !== false);
          setWeeklyLogbookHidden(!!d.hideWeeklyLogbookChecklist);
        }
      })
      .catch(() => setDrpMode('advanced'))
      .finally(() => setLoading(false));
  }, []);

  const setMode = async (mode) => {
    if (mode === drpMode) return;
    setSavingDrp(true);
    try {
      const r = await fetch(`${API}/system-settings/drp-mode`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ drpMode: mode })
      });
      const d = await r.json();
      if (d.success) {
        setDrpMode(d.drpMode);
        showToast(`DRP mode switched to ${mode === 'classic' ? 'Classic DRP' : 'Advanced DRP'}`);
      } else showToast(d.message || 'Failed to update');
    } catch { showToast('Failed to update DRP mode'); }
    setSavingDrp(false);
  };

  const toggleInternalTest = async () => {
    setSavingInternalTest(true);
    try {
      const r = await fetch(`${API}/system-settings/internal-test`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ enabled: !internalTestEnabled })
      });
      const d = await r.json();
      if (d.success) {
        setInternalTestEnabled(d.internalTestEnabled);
        showToast(`Internal Tests ${d.internalTestEnabled ? 'enabled' : 'disabled'}`);
      } else showToast(d.message || 'Failed to update');
    } catch { showToast('Failed to update'); }
    setSavingInternalTest(false);
  };

  const toggleWeeklyLogbook = async () => {
    setSavingWeeklyLogbook(true);
    try {
      const r = await fetch(`${API}/system-settings/weekly-logbook-checklist`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ hidden: !weeklyLogbookHidden })
      });
      const d = await r.json();
      if (d.success) {
        setWeeklyLogbookHidden(d.hideWeeklyLogbookChecklist);
        showToast(`"Weekly Logbook Submissions" ${d.hideWeeklyLogbookChecklist ? 'hidden from' : 'shown in'} the eligibility checklist`);
      } else showToast(d.message || 'Failed to update');
    } catch { showToast('Failed to update'); }
    setSavingWeeklyLogbook(false);
  };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60 }}>
      <FaSpinner style={{ animation: 'spin 1s linear infinite', fontSize: 28, color: '#b11217' }} />
      <style>{`@keyframes spin { from{transform:rotate(0deg);}to{transform:rotate(360deg);}}`}</style>
    </div>
  );

  const modes = [
    {
      key: 'advanced',
      title: 'Advanced DRP',
      description: 'Full-featured DRP system with geolocation attendance, DRPC verification, performance scoring, and automated certificate generation. Best for colleges with assigned DRPC coordinators.',
      features: ['Geolocation-based attendance', 'DRPC district coordinator verification', 'Performance scoring & grading', 'Weekly reports', 'Automated certificate with scores']
    },
    {
      key: 'classic',
      title: 'Classic DRP',
      description: 'Simple paper-based DRP tracking system. Students record attendance, daily diary, operations, and public health activities. DRP Incharge verifies and issues certificates.',
      features: ['Daily attendance with posting', 'Record of Daily Diary', 'Record of Operations', 'Public Health Activities', 'Incharge-verified certificate (PDF)']
    }
  ];

  return (
    <div style={s.container}>
      {toast && <div style={s.toast}>{toast}</div>}
      <style>{`@keyframes spin { from{transform:rotate(0deg);}to{transform:rotate(360deg);}}`}</style>

      <div style={s.pageHeader}>
        <div>
          <h1 style={s.title}>System Settings</h1>
          <p style={s.subtitle}>Configure DRP mode, internal tests, and the student eligibility checklist</p>
        </div>
      </div>

      {/* ── DRP Mode ─────────────────────────────────────────── */}
      <h2 style={s.sectionTitle}><FaHospital style={{ color: '#E67E22' }} /> DRP Mode</h2>

      <div style={s.currentBanner}>
        <FaCheckCircle style={{ color: '#059669', flexShrink: 0 }} />
        <span>
          Currently active: <strong>{drpMode === 'classic' ? 'Classic DRP' : 'Advanced DRP'}</strong>
          {' '}— {drpMode === 'classic' ? 'Students see the simplified DRP tracker.' : 'Students see the full-featured DRP system.'}
        </span>
      </div>

      <div style={s.infoBanner}>
        <FaInfoCircle style={{ color: '#2563eb', flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 13 }}>
          <strong>Note:</strong> Changing the DRP mode affects all students immediately. Existing data in both modes is preserved — switching back will restore the previous data.
        </div>
      </div>

      <div style={s.modesGrid}>
        {modes.map(mode => {
          const isActive = drpMode === mode.key;
          return (
            <div
              key={mode.key}
              style={{ ...s.modeCard, ...(isActive ? s.modeCardActive : {}) }}
              onClick={() => setMode(mode.key)}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: isActive ? '#b11217' : '#1a1a2e' }}>{mode.title}</h3>
                {savingDrp && mode.key !== drpMode ? (
                  <FaSpinner style={{ animation: 'spin 1s linear infinite', color: '#b11217' }} />
                ) : isActive ? (
                  <FaToggleOn style={{ fontSize: 28, color: '#059669' }} />
                ) : (
                  <FaToggleOff style={{ fontSize: 28, color: '#ccc' }} />
                )}
              </div>
              <p style={{ color: '#555', fontSize: 13, lineHeight: 1.6, marginBottom: 16 }}>{mode.description}</p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {mode.features.map(f => (
                  <li key={f} style={{ fontSize: 13, color: '#444', marginBottom: 4, lineHeight: 1.4 }}>{f}</li>
                ))}
              </ul>
              {isActive && (
                <div style={s.activeBadge}>
                  <FaCheckCircle style={{ marginRight: 6 }} /> Currently Active
                </div>
              )}
              {!isActive && (
                <button
                  style={s.switchBtn}
                  onClick={e => { e.stopPropagation(); setMode(mode.key); }}
                  disabled={savingDrp}
                >
                  {savingDrp ? <FaSpinner style={{ animation: 'spin 1s linear infinite', marginRight: 6 }} /> : null}
                  Switch to {mode.title}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Internal Tests ───────────────────────────────────── */}
      <h2 style={s.sectionTitle}><FaFileAlt style={{ color: '#E67E22' }} /> Internal Test Settings</h2>
      <ToggleCard
        title="Internal Tests"
        description={'When disabled, the "Internal Tests" section is hidden from the Teacher Dashboard and the Student Results section. Existing test data is preserved and will reappear if re-enabled.'}
        enabled={internalTestEnabled}
        saving={savingInternalTest}
        onToggle={toggleInternalTest}
      />

      {/* ── Exam Eligibility Checklist ───────────────────────── */}
      <h2 style={s.sectionTitle}><FaClipboardCheck style={{ color: '#E67E22' }} /> Exam Eligibility Checklist</h2>
      <ToggleCard
        title="Show Weekly Logbook Submissions"
        description={'When disabled, the "Weekly Logbook Submissions" requirement is hidden from the student dashboard\'s eligibility checklist and no longer counted towards a student\'s overall eligibility status.'}
        enabled={!weeklyLogbookHidden}
        saving={savingWeeklyLogbook}
        onToggle={toggleWeeklyLogbook}
        enabledLabel="Currently Shown"
        disabledLabel="Currently Hidden"
      />
    </div>
  );
};

const s = {
  container: { maxWidth: 860, margin: '0 auto', padding: '24px 16px' },
  pageHeader: { display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 20 },
  title: { margin: 0, fontSize: 22, fontWeight: 800, color: '#1a1a2e' },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: '#666' },
  sectionTitle: { display: 'flex', alignItems: 'center', gap: 10, fontSize: 16, fontWeight: 800, color: '#1a1a2e', margin: '28px 0 12px' },
  currentBanner: { display: 'flex', gap: 10, alignItems: 'center', padding: '12px 16px', background: '#d1fae5', border: '1px solid #a7f3d0', borderRadius: 10, marginBottom: 12, fontSize: 14 },
  infoBanner: { display: 'flex', gap: 10, padding: '12px 16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, marginBottom: 20 },
  modesGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 },
  modeCard: { background: '#fff', border: '2px solid #e5e7eb', borderRadius: 14, padding: 20, cursor: 'pointer', transition: 'border-color 0.2s, box-shadow 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  modeCardActive: { border: '2px solid #b11217', boxShadow: '0 4px 16px rgba(177,18,23,0.15)' },
  activeBadge: { display: 'inline-flex', alignItems: 'center', marginTop: 16, fontSize: 12, fontWeight: 700, color: '#059669', background: '#d1fae5', padding: '4px 12px', borderRadius: 20 },
  switchBtn: { display: 'block', width: '100%', marginTop: 16, padding: '10px', background: '#b11217', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 20, marginBottom: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  toast: { position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: '#1a1a2e', color: '#fff', padding: '10px 20px', borderRadius: 20, fontSize: 14, zIndex: 2000, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }
};

export default SystemSettings;
